import { Link } from 'react-router-dom';
import { useState } from 'react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/shared/SEOHead';
import { DocsNav } from '@/components/docs/DocsNav';
import { DocsSidebarNav, SidebarSection } from '@/components/docs/DocsSidebarNav';
import { useGuides } from '@/hooks/useDocumentation';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Book, FileText, ArrowRight, Menu } from 'lucide-react';
import * as Icons from 'lucide-react';
import { useMemo } from 'react';

export default function GuidesIndex() {
  const { data, isLoading, error } = useGuides();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Build sidebar sections from categories and docs
  const sidebarSections: SidebarSection[] = useMemo(() => {
    if (!data) return [];
    
    return data.categories.map((category) => ({
      title: category.name,
      slug: category.slug,
      items: data.docs
        .filter((doc) => doc.category_id === category.id)
        .map((doc) => ({
          title: doc.title,
          slug: doc.slug,
          href: `/docs/guides/${doc.slug}`,
        })),
    }));
  }, [data]);
  
  // Group docs by category for main content
  const docsByCategory = useMemo(() => {
    if (!data) return {};
    
    return data.categories.reduce((acc, category) => {
      acc[category.id] = {
        category,
        docs: data.docs.filter((doc) => doc.category_id === category.id),
      };
      return acc;
    }, {} as Record<string, { category: typeof data.categories[0]; docs: typeof data.docs }>);
  }, [data]);
  
  const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
    FileText: Icons.FileText,
    Book: Icons.Book,
    Code: Icons.Code,
    Settings: Icons.Settings,
    Rocket: Icons.Rocket,
    Shield: Icons.Shield,
    Database: Icons.Database,
    Zap: Icons.Zap,
    Layout: Icons.Layout,
    Globe: Icons.Globe,
  };
  
  const getIconComponent = (iconName: string | null) => {
    if (!iconName) return FileText;
    return iconMap[iconName] || FileText;
  };
  
  return (
    <Layout>
      <SEOHead path="/docs/guides" />
      
      <DocsNav />
      <div className="container py-8">
        <div className="flex gap-8">
          {/* Sidebar */}
          <DocsSidebarNav sections={sidebarSections} className="hidden lg:block" />
          
          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                  <Book className="h-5 w-5 text-primary" />
                </div>
                <h1 className="text-3xl font-bold">Guides</h1>
              </div>
              <p className="text-muted-foreground max-w-2xl">
                Learn how to implement automotive data standards with our comprehensive guides. 
                From getting started to advanced integration patterns.
              </p>
            </div>
            
            {/* Loading State */}
            {isLoading && (
              <div className="space-y-8">
                {[1, 2].map((i) => (
                  <div key={i}>
                    <Skeleton className="h-6 w-40 mb-4" />
                    <div className="grid gap-4 md:grid-cols-2">
                      <Skeleton className="h-32" />
                      <Skeleton className="h-32" />
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Error State */}
            {error && (
              <div className="text-center py-12">
                <p className="text-destructive">Failed to load guides. Please try again.</p>
              </div>
            )}
            
            {/* Empty State */}
            {data && data.docs.length === 0 && (
              <div className="text-center py-12 border rounded-lg bg-muted/20">
                <Book className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No guides yet</h3>
                <p className="text-muted-foreground">
                  Check back soon for documentation guides.
                </p>
              </div>
            )}
            
            {/* Categories with Docs */}
            {data && Object.values(docsByCategory).map(({ category, docs }) => {
              if (docs.length === 0) return null;
              
              const CategoryIcon = getIconComponent(category.icon);
              
              return (
                <section key={category.id} className="mb-10">
                  <div className="flex items-center gap-2 mb-4">
                    <CategoryIcon className="h-5 w-5 text-muted-foreground" />
                    <h2 className="text-xl font-semibold">{category.name}</h2>
                    <Badge variant="secondary" className="ml-2">
                      {docs.length} {docs.length === 1 ? 'guide' : 'guides'}
                    </Badge>
                  </div>
                  
                  {category.description && (
                    <p className="text-sm text-muted-foreground mb-4">
                      {category.description}
                    </p>
                  )}
                  
                  <div className="grid gap-4 md:grid-cols-2">
                    {docs.map((doc) => (
                      <Link key={doc.id} to={`/docs/guides/${doc.slug}`} className="group">
                        <Card className="h-full transition-all hover:shadow-md hover:border-primary/50">
                          <CardHeader className="pb-4">
                            <CardTitle className="text-base flex items-center gap-2">
                              {doc.title}
                              <ArrowRight className="h-4 w-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                            </CardTitle>
                            {doc.excerpt && (
                              <CardDescription className="text-sm line-clamp-2">
                                {doc.excerpt}
                              </CardDescription>
                            )}
                          </CardHeader>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </section>
              );
            })}
          </main>
        </div>
      </div>

      {/* Mobile floating nav */}
      <div className="lg:hidden fixed bottom-4 right-4 z-50">
        <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
          <SheetTrigger asChild>
            <Button size="icon" className="rounded-full shadow-lg h-12 w-12">
              <Menu className="h-5 w-5" />
              <span className="sr-only">Toggle guides menu</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 pt-12">
            <h3 className="font-semibold mb-4 px-3">Guides</h3>
            <DocsSidebarNav 
              sections={sidebarSections} 
              onItemClick={() => setSidebarOpen(false)}
            />
          </SheetContent>
        </Sheet>
      </div>
    </Layout>
  );
}
